import { useEffect, useState, useRef, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import './VoiceRecorder.css';

// ─── MIME Type Helpers ───────────────────────────────────────────────────────
// Prefer m4a (AAC) for cross-platform compatibility (iOS, Android, Safari, Chrome).
// Fall back to webm for browsers that don't support mp4 recording (Firefox).
const MIME_PRIORITY = ['audio/mp4', 'audio/mp4;codecs=mp4a.40.2', 'audio/aac', 'audio/webm;codecs=opus', 'audio/webm'];

function getPreferredMimeType() {
    for (const mime of MIME_PRIORITY) {
        if (MediaRecorder.isTypeSupported(mime)) return mime;
    }
    return ''; // let the browser pick its default
}

/**
 * Returns the file extension for a given MIME type from MediaRecorder.
 * Use this when building the File object before upload.
 */
export function getFileExtFromMime(mimeType) {
    if (mimeType.startsWith('audio/mp4') || mimeType.startsWith('audio/aac')) return 'm4a';
    if (mimeType.startsWith('audio/webm')) return 'webm';
    return 'audio';
}

// ─── Voice Recorder Hook ────────────────────────────────────────────────────
export function useVoiceRecorder() {
    const [state, setState] = useState('idle'); // idle | recording | paused | recorded
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [duration, setDuration] = useState(0);
    const [analyser, setAnalyser] = useState(null);

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const timerRef = useRef(null);
    const audioContextRef = useRef(null);

    const startTimer = useCallback(() => {
        timerRef.current = setInterval(() => {
            setDuration((prev) => prev + 1);
        }, 1000);
    }, []);

    const stopTimer = useCallback(() => {
        clearInterval(timerRef.current);
        timerRef.current = null;
    }, []);

    const startRecording = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioContext;
            const source = audioContext.createMediaStreamSource(stream);
            const analyserNode = audioContext.createAnalyser();
            analyserNode.fftSize = 256;
            source.connect(analyserNode);
            setAnalyser(analyserNode);

            // Prefer mp4/m4a (works on iOS + most browsers), fall back to webm (Firefox)
            const mimeType = getPreferredMimeType();

            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType });
                setAudioBlob(blob);
                setAudioUrl(URL.createObjectURL(blob));
            };

            mediaRecorder.start(100);
            setDuration(0);
            startTimer();
            setState('recording');
        } catch (err) {
            console.error('Microphone access denied:', err);
            throw err;
        }
    }, [startTimer]);

    const pauseRecording = useCallback(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.pause();
            stopTimer();
            setState('paused');
        }
    }, [stopTimer]);

    const resumeRecording = useCallback(() => {
        if (mediaRecorderRef.current?.state === 'paused') {
            mediaRecorderRef.current.resume();
            startTimer();
            setState('recording');
        }
    }, [startTimer]);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        stopTimer();
        streamRef.current?.getTracks().forEach((track) => track.stop());
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        setAnalyser(null);
        setState('recorded');
    }, [stopTimer]);

    const discardRecording = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        streamRef.current?.getTracks().forEach((track) => track.stop());
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        stopTimer();
        setAnalyser(null);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioBlob(null);
        setAudioUrl(null);
        setDuration(0);
        setState('idle');
    }, [audioUrl, stopTimer]);

    return {
        state,
        audioBlob,
        audioUrl,
        duration,
        analyser,
        startRecording,
        pauseRecording,
        resumeRecording,
        stopRecording,
        discardRecording,
    };
}

// ─── Live Waveform Visualizer ───────────────────────────────────────────────
function Waveform({ analyser }) {
    const canvasRef = useRef(null);
    const animFrameRef = useRef(null);

    useEffect(() => {
        if (!analyser) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
            animFrameRef.current = requestAnimationFrame(draw);
            analyser.getByteFrequencyData(dataArray);

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const barCount = 40;
            const barWidth = canvas.width / barCount - 2;
            const step = Math.floor(bufferLength / barCount);

            for (let i = 0; i < barCount; i++) {
                const value = dataArray[i * step];
                const barHeight = (value / 255) * canvas.height * 0.9;
                const x = i * (barWidth + 2);
                const y = (canvas.height - barHeight) / 2;

                ctx.fillStyle = '#3b82f6';
                ctx.beginPath();
                ctx.roundRect(x, y, barWidth, barHeight || 2, 2);
                ctx.fill();
            }
        };

        draw();
        return () => cancelAnimationFrame(animFrameRef.current);
    }, [analyser]);

    return <canvas ref={canvasRef} width={200} height={36} style={{ display: 'block', borderRadius: '4px' }} />;
}

// ─── Playback Waveform (static bars + progress) ────────────────────────────
function PlaybackWaveform({ progress }) {
    const bars = 40;
    const heights = useRef(Array.from({ length: bars }, (_, i) => 0.2 + Math.abs(Math.sin(i * 1.8)) * 0.8)).current;

    const filledBars = Math.floor(progress * bars);

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5px', height: '36px' }}>
            {heights.map((h, i) => (
                <div
                    key={i}
                    style={{
                        width: '3px',
                        height: `${h * 100}%`,
                        borderRadius: '2px',
                        backgroundColor: i <= filledBars ? '#3b82f6' : '#cbd5e1',
                        transition: 'background-color 0.1s',
                    }}
                />
            ))}
        </div>
    );
}

// ─── Format seconds → mm:ss ─────────────────────────────────────────────────
function formatTime(seconds) {
    const m = Math.floor(seconds / 60)
        .toString()
        .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
}

// ─── SVG Icons ──────────────────────────────────────────────────────────────
const Icons = {
    close: (
        <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    ),
    pause: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
    ),
    play: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5,3 19,12 5,21" />
        </svg>
    ),
    stop: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <rect x="4" y="4" width="16" height="16" rx="2" />
        </svg>
    ),
    send: (
        <svg width="18" height="18" viewBox="0 0 535.5 535.5" fill="#fff">
            <polygon points="0,497.25 535.5,267.75 0,38.25 0,216.75 382.5,267.75 0,318.75" />
        </svg>
    ),
    mic: (
        <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect x="9" y="1" width="6" height="12" rx="3" />
            <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
            <line x1="12" y1="18" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
    ),
};

// ─── Main VoiceRecorder Component ───────────────────────────────────────────
//
// Props:
//   onSend(audioBlob: Blob)  – called with the recorded audio blob when user hits send
//   onCancel()               – called when user discards or mic access fails
//   isUploading?: boolean    – show spinner on send button while parent is uploading
//
function VoiceRecorder({ onSend, onCancel, isUploading = false }) {
    const {
        state,
        audioBlob,
        audioUrl,
        duration,
        analyser,
        startRecording,
        pauseRecording,
        resumeRecording,
        stopRecording,
        discardRecording,
    } = useVoiceRecorder();

    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackProgress, setPlaybackProgress] = useState(0);

    // Auto-start recording on mount
    useEffect(() => {
        startRecording().catch(() => {
            onCancel?.();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Playback progress tracking
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onTimeUpdate = () => {
            if (audio.duration) {
                setPlaybackProgress(audio.currentTime / audio.duration);
            }
        };
        const onEnded = () => {
            setIsPlaying(false);
            setPlaybackProgress(0);
        };

        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('ended', onEnded);
        return () => {
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('ended', onEnded);
        };
    }, [audioUrl]);

    const togglePlayback = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            audio.play();
            setIsPlaying(true);
        }
    };

    const handleSend = () => {
        if (audioBlob) onSend?.(audioBlob);
    };

    const handleDiscard = () => {
        discardRecording();
        onCancel?.();
    };

    // ── Recording in progress ──
    if (state === 'recording' || state === 'paused') {
        return (
            <div className="voice-recorder-bar">
                <button type="button" className="voice-btn voice-btn-discard" onClick={handleDiscard} title="Discard">
                    {Icons.close}
                </button>

                <span className="voice-timer">{formatTime(duration)}</span>

                <div className="voice-waveform-container">
                    {state === 'recording' && analyser ? (
                        <Waveform analyser={analyser} />
                    ) : (
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>Paused</span>
                    )}
                </div>

                <button
                    type="button"
                    className="voice-btn voice-btn-pause"
                    onClick={state === 'recording' ? pauseRecording : resumeRecording}
                    title={state === 'recording' ? 'Pause' : 'Resume'}
                >
                    {state === 'recording' ? Icons.pause : Icons.play}
                </button>

                <button
                    type="button"
                    className="voice-btn voice-btn-stop"
                    onClick={stopRecording}
                    title="Stop recording"
                >
                    {Icons.stop}
                </button>
            </div>
        );
    }

    // ── Recorded – preview & send ──
    if (state === 'recorded' && audioUrl) {
        return (
            <div className="voice-recorder-bar">
                <audio ref={audioRef} src={audioUrl} preload="metadata" />

                <button type="button" className="voice-btn voice-btn-discard" onClick={handleDiscard} title="Discard">
                    {Icons.close}
                </button>

                <button
                    type="button"
                    className="voice-btn voice-btn-play"
                    onClick={togglePlayback}
                    title={isPlaying ? 'Pause' : 'Play'}
                >
                    {isPlaying ? Icons.pause : Icons.play}
                </button>

                <div className="voice-waveform-container">
                    <PlaybackWaveform progress={playbackProgress} />
                </div>

                <span className="voice-timer">{formatTime(duration)}</span>

                <button
                    type="button"
                    className="voice-btn voice-btn-send"
                    onClick={handleSend}
                    disabled={isUploading}
                    title="Send voice message"
                >
                    {isUploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : Icons.send}
                </button>
            </div>
        );
    }

    return null;
}

// ─── Mic Trigger Button (convenience export) ────────────────────────────────
//
// Drop this wherever you need a mic icon that activates recording.
// Usage: <VoiceRecorderMicButton onClick={() => setIsRecording(true)} />
//
export function VoiceRecorderMicButton({ onClick, className = '', ...rest }) {
    return (
        <span
            className={`voice-mic-btn cursor_pointer ${className}`}
            onClick={onClick}
            title="Record voice message"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onClick?.(e);
            }}
            {...rest}
        >
            {Icons.mic}
        </span>
    );
}

export default VoiceRecorder;
